import React, { useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import { Toast } from "primereact/toast";
import {
  fetchStarmeterExtractor,
  fetchCrawlerStatus,
  runStarmeterCrawler,
} from "../../service/STARMeterExtratorService";
import "./index.css";

const professionOptions = [
  { label: "Actor", value: "Actor" },
  { label: "Actress", value: "Actress" },
  { label: "Director", value: "Director" },
  { label: "Writer", value: "Writer" },
];

const sortFieldOptions = [
  { label: "Name", value: "name" },
  { label: "Profession", value: "profession" },
  { label: "STARmeter", value: "starmeter" },
  { label: "Know for titles", value: "know_for_titles" },
];

const sortOrderOptions = [
  { label: "Ascendente", value: "asc" },
  { label: "Descendente", value: "desc" },
];

const startYear = 2020;
const currentYear = new Date().getFullYear();
const yearOptions = [];

for (let year = startYear; year <= currentYear; year++) {
  yearOptions.push({ label: String(year), value: year });
}

export default function STARMeterExtractor() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [profession, setProfession] = useState("Actor");
  const [search, setSearch] = useState("");
  const [starmeterMin, setStarmeterMin] = useState(null);
  const [starmeterMax, setStarmeterMax] = useState(null);
  const [sortField, setSortField] = useState("professional_name");
  const [sortOrder, setSortOrder] = useState("asc");

  const [loadingProfs, setLoadingProfs] = useState({});

  const [crawlerConfig, setCrawlerConfig] = useState({
    Actor: {
      min: 1,
      max: 200,
      minCredits: "",
      startYear: startYear,
      endYear: currentYear,
      lastUpdate: "-",
    },
    Actress: {
      min: 1,
      max: 200,
      minCredits: "",
      startYear: startYear,
      endYear: currentYear,
      lastUpdate: "-",
    },
    Director: {
      min: 1,
      max: 200,
      minCredits: "",
      startYear: startYear,
      endYear: currentYear,
      lastUpdate: "-",
    },
    Writer: {
      min: 1,
      max: 200,
      minCredits: "",
      startYear: startYear,
      endYear: currentYear,
      lastUpdate: "-",
    },
  });

  const queryParams = useMemo(
    () => ({
      profession: profession,
      search_query: search || undefined,
      starmeter_min: starmeterMin ?? undefined,
      starmeter_max: starmeterMax ?? undefined,
      sort_field: sortField,
      sort_order: sortOrder,
      limit: 99999,
    }),
    [profession, search, starmeterMin, starmeterMax, sortField, sortOrder],
  );

  const loadData = async (customParams = queryParams) => {
    setLoading(true);
    try {
      const data = await fetchStarmeterExtractor(customParams);
      setRows(data.data || []);
      setTotalRecords(data.meta?.total || 0);
    } catch (error) {
      console.error("Error loading STARmeter Extractor:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [queryParams]);

  useEffect(() => {
    loadCrawlerStatus();
  }, []);

  const onSearchSubmit = () => {
    setSearch(searchInput.trim());
  };

  const onClearFilters = () => {
    setProfession("Actor");
    setSearchInput("");
    setSearch("");
    setStarmeterMin(null);
    setStarmeterMax(null);
    setSortField("professional_name");
    setSortOrder("asc");
  };

  const exportToXlsx = async () => {
    try {
      setLoading(true);
      const data = await fetchStarmeterExtractor(queryParams);

      const exportRows = (data.data || []).map((item) => ({
        "Professional Name": item.professional_name,
        Profession: item.profession,
        STARmeter: item.starmeter,
        "Know for titles": item.known_for,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "STARmeter");
      XLSX.writeFile(workbook, "STARmeter.xlsx");
    } catch (error) {
      console.error("Error exporting XLSX:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCrawlerAction = async (prof) => {
    const config = crawlerConfig[prof];

    setLoadingProfs((prev) => ({ ...prev, [prof]: true }));
    try {
      const params = {
        professions: [prof],
        starmeter_min: config.min,
        starmeter_max: config.max,
        min_credits: config.minCredits,
        year_min: config.startYear,
        year_max: config.endYear,
      };

      await runStarmeterCrawler(params);
      await loadCrawlerStatus();

      toast.current.show({
        severity: "success",
        summary: "Crawler Started",
        detail: `The extraction for ${prof} has been successfully triggered.`,
        life: 4000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: `Failed to start crawler for ${prof}. Please try again.`,
        life: 5000,
      });
      console.error(` Error running crawler for ${prof}:`, error);
    } finally {
      setLoadingProfs((prev) => ({ ...prev, [prof]: false }));
    }
  };

  const updateConfig = (prof, field, value) => {
    setCrawlerConfig((prev) => ({
      ...prev,
      [prof]: { ...prev[prof], [field]: value },
    }));
  };

  const loadCrawlerStatus = async () => {
    try {
      const data = await fetchCrawlerStatus();

      setCrawlerConfig((prev) => {
        const newConfig = { ...prev };
        Object.keys(data).forEach((prof) => {
          if (newConfig[prof]) {
            newConfig[prof].lastUpdate = data[prof];
          }
        });
        return newConfig;
      });
    } catch (error) {
      console.error("Error loading crawler status:", error);
    }
  };

  return (
    <div className="grid starmeter-gap-page">
      <Toast ref={toast} />

      <div className="col-12">
        <Card
          title="STARmeter Extractor"
          subTitle="A tool to extract data from STARmeter IMDb PRO to automate talent relevance analysis and accelerate decision-making in new licensing."
        >
          <TabView>
            <TabPanel header="Search Database" leftIcon="pi pi-search mr-2">
              <div className="starmeter-gap-toolbar">
                <Dropdown
                  value={profession}
                  options={professionOptions}
                  onChange={(e) => setProfession(e.value)}
                  placeholder="Profession"
                />

                <span className="p-input-icon-left">
                  <i className="pi pi-search" />
                  <InputText
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
                    placeholder="Enter name or known for titles and press Enter to search"
                  />
                </span>

                <InputNumber
                  value={starmeterMin}
                  onValueChange={(e) => setStarmeterMin(e.value)}
                  placeholder="STARmeter min."
                  useGrouping={false}
                />

                <InputNumber
                  value={starmeterMax}
                  onValueChange={(e) => setStarmeterMax(e.value)}
                  placeholder="STARmeter max."
                  useGrouping={false}
                />

                <Dropdown
                  value={sortField}
                  options={sortFieldOptions}
                  onChange={(e) => setSortField(e.value)}
                  placeholder="Ordenar por"
                />

                <Dropdown
                  value={sortOrder}
                  options={sortOrderOptions}
                  onChange={(e) => setSortOrder(e.value)}
                  placeholder="Ordem"
                />

                <Button
                  label="Search"
                  icon="pi pi-search"
                  onClick={onSearchSubmit}
                />
                <Button
                  label="Clear Filters"
                  icon="pi pi-times"
                  className="p-button-secondary"
                  onClick={onClearFilters}
                />
                <Button
                  label="Export to XLSX"
                  icon="pi pi-file-excel"
                  className="p-button-success"
                  onClick={exportToXlsx}
                />
              </div>

              <DataTable
                value={rows}
                loading={loading}
                lazy
                totalRecords={totalRecords}
                responsiveLayout="scroll"
                stripedRows
                showGridlines
                emptyMessage="No data found."
              >
                <Column
                  header="Name"
                  field="professional_name"
                  style={{ width: "22rem" }}
                />
                <Column
                  header="Profession"
                  field="profession"
                  style={{ minWidth: "22rem" }}
                />
                <Column
                  field="starmeter"
                  header="STARmeter"
                  style={{ minWidth: "10rem" }}
                />
                <Column
                  field="known_for"
                  header="Position Known For Titles"
                  style={{ minWidth: "30rem" }}
                />
              </DataTable>
            </TabPanel>

            <TabPanel header="Crawler Setup" leftIcon="pi pi-cog mr-2">
              <div className="p-4">
                <div className="grid font-bold mb-3 border-bottom-1 pb-2">
                  <div className="col-1 text-center">Profession</div>
                  <div className="col-3 text-center">STARmeter range</div>
                  <div className="col-1 text-center">Min. credits</div>
                  <div className="col-3 text-center">Specific year range</div>
                  <div className="col-2 text-center">Last update</div>
                  <div className="col-2 text-center">Action</div>
                </div>

                {Object.keys(crawlerConfig).map((prof) => (
                  <div
                    key={prof}
                    className="grid align-items-center mb-3 p-2 border-round hover:surface-100"
                  >
                    {/* 1. Profession */}
                    <div className="col-1 text-center">
                      <Tag
                        value={prof}
                        severity="info"
                        className="crawler-tag"
                      />
                    </div>

                    {/* 2. STARmeter range */}
                    <div className="col-3 starmeter-range-container justify-content-center">
                      <InputNumber
                        value={crawlerConfig[prof].min}
                        onValueChange={(e) =>
                          updateConfig(prof, "min", e.value)
                        }
                        useGrouping={false}
                      />
                      <span>to</span>
                      <InputNumber
                        value={crawlerConfig[prof].max}
                        onValueChange={(e) =>
                          updateConfig(prof, "max", e.value)
                        }
                        useGrouping={false}
                      />
                    </div>

                    {/* 3. Min. Credits */}
                    <div className="col-1 text-center crawler-min-credits-col">
                      <InputNumber
                        value={crawlerConfig[prof].minCredits}
                        onValueChange={(e) =>
                          updateConfig(prof, "minCredits", e.value)
                        }
                        useGrouping={false}
                      />
                    </div>

                    {/* 4. Year range */}
                    <div className="col-3 starmeter-range-container justify-content-center">
                      <Dropdown
                        options={yearOptions}
                        value={crawlerConfig[prof].startYear}
                        onChange={(e) =>
                          updateConfig(prof, "startYear", e.value)
                        }
                      />
                      <span>to</span>
                      <Dropdown
                        options={yearOptions}
                        value={crawlerConfig[prof].endYear}
                        onChange={(e) => updateConfig(prof, "endYear", e.value)}
                      />
                    </div>

                    {/* 5. Last update */}
                    <div className="col-2 text-center">
                      <span className="p-disabled">
                        {crawlerConfig[prof].lastUpdate}
                      </span>
                    </div>

                    {/* 6. Action */}
                    <div className="col-2 text-center">
                      <Button
                        label="Update manual"
                        icon="pi pi-play"
                        className="p-button-sm p-button-secondary crawler-btn-action"
                        onClick={() => handleCrawlerAction(prof)}
                        loading={loadingProfs[prof]}
                        disabled={Object.values(loadingProfs).some(
                          (isLoading) => isLoading,
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm font-italic crawler-footer-note">
                * This routine automatically imports the data configured above,
                weekly, every Monday at 8 AM.
              </p>
            </TabPanel>
          </TabView>
        </Card>
      </div>
    </div>
  );
}
